import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import { ProcessOfferEmailVariantDto, ProcessOfferItemDto } from '@/api/generated';
import { useAttachOfferToEmail } from '@/components/emailWindow/components/attachments/components/attachOffer/util/useAttachOfferToEmail.util.ts';
import { NewEmailAttachmentModel } from '@/components/emailWindow/components/attachments/model/NewEmailAttachment.model.ts';
import { ComponentSpinner } from '@/components/ui/componentSpinner/ComponentSpinner.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import {
  NEW_EMAIL_REDUCER_EVENT,
  useNewEmailUpdateState,
} from '@/views/emails/context/NewEmails.ts';
import { ProcessMultiOfferGroupAccordion } from '@/views/processesDetails/components/multiOffers/comp/ProcessMultiOfferGroupAccordion.comp.tsx';
import { useGetProcessMultiOffers } from '@/views/processesDetails/components/multiOffers/util/useGetProcessMultiOffers.util.ts';

interface Props {
  emailId: string;
  onClose: () => void;
  processId: string;
}

const PREVIEW_DRAWER_OVER_DIALOG_SX = { zIndex: 1400 };

const ATTACH_BUTTON_SX = {
  fontSize: '11px',
  lineHeight: 1.2,
  minHeight: '24px',
  minWidth: 0,
  px: 1,
  py: 0.25,
  textTransform: 'none' as const,
};

export const AttachOfferDialog: React.FC<Props> = ({ emailId, onClose, processId }) => {
  const { t } = useTranslationWithPrefix('views.emails.newEmail.attachOffer.dialog');
  const { isPending, multiOffers } = useGetProcessMultiOffers({ processId });
  const { attachOffer, isAttachingOffer } = useAttachOfferToEmail();
  const updateNewEmailState = useNewEmailUpdateState();

  const handleAttach = async (offer: ProcessOfferItemDto, variant: ProcessOfferEmailVariantDto) => {
    const uuid = crypto.randomUUID();
    const optimistic: NewEmailAttachmentModel = {
      contentType: 'application/pdf',
      isReady: false,
      name: `${offer.name}.pdf`,
      uuid,
    };
    updateNewEmailState({
      payload: optimistic,
      type: NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_ADD,
    });
    try {
      const result = await attachOffer({
        emailId,
        processId,
        processOfferId: offer.processOfferId,
        variant,
      });
      updateNewEmailState({
        payload: { attachmentId: result.attachmentId, name: result.fileName, uuid },
        type: NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_UPDATE_ID,
      });
      onClose();
    } catch (e) {
      updateNewEmailState({
        payload: { uuid },
        type: NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_REMOVE,
      });
      throw e;
    }
  };

  const renderAttachActions = (offer: ProcessOfferItemDto) => (
    <>
      <Button
        disabled={isAttachingOffer}
        onClick={() => handleAttach(offer, ProcessOfferEmailVariantDto.Offer)}
        size="small"
        sx={ATTACH_BUTTON_SX}
        variant="outlined"
      >
        {t('button.attachOffer')}
      </Button>
      <Button
        disabled={isAttachingOffer}
        onClick={() => handleAttach(offer, ProcessOfferEmailVariantDto.RealizationOffer)}
        size="small"
        sx={ATTACH_BUTTON_SX}
        variant="outlined"
      >
        {t('button.attachRealization')}
      </Button>
    </>
  );

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      onClose={onClose}
      open
      slotProps={{ paper: { sx: { maxWidth: '1440px', width: '95vw' } } }}
    >
      <DialogTitle>
        <Typography variant="f20Regular">{t('title')}</Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: '320px', position: 'relative' }}>
        <ComponentSpinner isVisible={isPending || isAttachingOffer} />
        {!isPending && multiOffers.length === 0 && (
          <Typography sx={{ color: themeColors.greyDark, py: 4, textAlign: 'center' }}>
            {t('empty')}
          </Typography>
        )}
        <Stack spacing={1.5}>
          {multiOffers.map(group => (
            <ProcessMultiOfferGroupAccordion
              group={group}
              key={group.groupId}
              previewDrawerSx={PREVIEW_DRAWER_OVER_DIALOG_SX}
              renderExtraActions={renderAttachActions}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={isAttachingOffer} onClick={onClose} variant="outlined">
          {t('button.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
